import React from 'react';
import { HolderOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Input, Space, Switch, Table, Typography } from 'antd';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';

const { Search } = Input;
const { Text } = Typography;

const DraggableRow = ({ children, ...props }) => {
  const rowKey = props['data-row-key'];
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: String(rowKey ?? ''),
    disabled: !rowKey,
  });

  const style = {
    ...props.style,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...(rowKey ? attributes : {})}>
      {React.Children.map(children, (child) => {
        if (child && child.key === 'drag') {
          return React.cloneElement(child, {
            children: (
              <HolderOutlined
                ref={setActivatorNodeRef}
                style={{ cursor: 'grab', touchAction: 'none', fontSize: 16, color: '#bbb' }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const TagDesktop = ({ viewModel }) => {
  const { data, filter, setFilterValue, createTag, editTag, deleteTag, toggleVisible, reorderItems } = viewModel;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = ({ active, over }) => {
    if (!active || !over || active.id === over.id) return;
    reorderItems(String(active.id), String(over.id));
  };

  const columns = [
    {
      key: 'drag',
      width: 40,
      align: 'center',
    },
    {
      title: 'Tag',
      dataIndex: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Hiển thị web',
      width: 130,
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={!!record.is_visible}
          onChange={() => toggleVisible(record)}
          checkedChildren='Hiện'
          unCheckedChildren='Ẩn'
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Space size={4}>
          <Button type='link' size='small' icon={<EditOutlined />} onClick={() => editTag(record.id)} />
          <Button type='link' size='small' danger icon={<DeleteOutlined />} onClick={() => deleteTag(record.id)} />
        </Space>
      ),
    },
  ];

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <Card>
        <Flex justify='space-between' align='center' gap={12}>
          <Search
            placeholder='Tên tag...'
            onChange={(e) => setFilterValue('info', e.target.value)}
            value={filter.info}
            style={{ maxWidth: 320 }}
            allowClear
          />
          <Button type='primary' onClick={createTag}>Thêm mới</Button>
        </Flex>
      </Card>

      <Card>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={data.map((r) => String(r.id))} strategy={verticalListSortingStrategy}>
            <Table
              rowKey='id'
              columns={columns}
              dataSource={data}
              pagination={false}
              size='middle'
              components={{ body: { row: DraggableRow } }}
            />
          </SortableContext>
        </DndContext>
      </Card>
    </Space>
  );
};

export default TagDesktop;
